import React, { useState, useRef, useEffect } from 'react';
import { FormGroup, Label } from 'reactstrap';
import './TimePickerField.scss';

interface TimePickerFieldProps {
    label: string;
    name: string;
    value: string;
    onChange: (name: string, value: string) => void;
    disabled?: boolean;
    placeholder?: string;
    error?: boolean;
    errorMessage?: string;
    /** Orange text when form time is outside availability block range */
    mismatchWarning?: boolean;
}

export const TimePickerField: React.FC<TimePickerFieldProps> = ({
    label,
    name,
    value,
    onChange,
    disabled = false,
    placeholder = 'Select time',
    error = false,
    errorMessage,
    mismatchWarning = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedHour, setSelectedHour] = useState('');
    const [selectedMinute, setSelectedMinute] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState('AM');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);

    // Parse value into hour, minute, period
    useEffect(() => {
        if (value) {
            const [hourStr, minuteStr] = value.split(':');
            const hour24 = parseInt(hourStr, 10);
            const minute = minuteStr;
            
            const period = hour24 >= 12 ? 'PM' : 'AM';
            const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
            
            setSelectedHour(hour12.toString().padStart(2, '0'));
            setSelectedMinute(minute);
            setSelectedPeriod(period);
        } else {
            setSelectedHour('');
            setSelectedMinute('');
            setSelectedPeriod('AM');
        }
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Generate hours (01-12)
    const hours = Array.from({ length: 12 }, (_, i) => {
        const h = i + 1;
        return h.toString().padStart(2, '0');
    });

    // Minutes: 00, 15, 30, 45
    const minutes = ['00', '15', '30', '45'];

    const handleTimeSelect = (hour: string, minute: string, period: string) => {
        let hour24 = parseInt(hour, 10);
        if (period === 'AM') {
            if (hour24 === 12) hour24 = 0;
        } else {
            if (hour24 !== 12) hour24 += 12;
        }

        const timeString = `${hour24.toString().padStart(2, '0')}:${minute}`;
        onChange(name, timeString);
    };

    const handleHourClick = (hour: string) => {
        setSelectedHour(hour);
        if (selectedMinute) {
            handleTimeSelect(hour, selectedMinute, selectedPeriod);
        }
    };

    const handleMinuteClick = (minute: string) => {
        setSelectedMinute(minute);
        if (selectedHour) {
            handleTimeSelect(selectedHour, minute, selectedPeriod);
        }
    };

    const handlePeriodClick = (period: string) => {
        setSelectedPeriod(period);
        if (selectedHour && selectedMinute) {
            handleTimeSelect(selectedHour, selectedMinute, period);
        }
    };

    const displayValue = value
        ? `${selectedHour}:${selectedMinute} ${selectedPeriod}`
        : '';

    return (
        <FormGroup className={`time-picker-field ${error ? 'has-error' : ''} ${mismatchWarning ? 'availability-mismatch' : ''}`}>
            <Label className="time-picker-label">{label}</Label>
            <div className="time-picker-input-wrapper">
                <div
                    ref={inputRef}
                    className={`custom-time-input ${error ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''} ${mismatchWarning ? 'availability-mismatch' : ''}`}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                >
                    <span className={`time-display ${!displayValue ? 'placeholder' : ''} ${mismatchWarning ? 'availability-mismatch' : ''}`}>
                        {displayValue || placeholder}
                    </span>
                    <svg className="time-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0zm0 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.5 2v5.5h3v1h-4V3h1z"/>
                    </svg>
                </div>

                {isOpen && !disabled && (
                    <div ref={dropdownRef} className="time-picker-dropdown">
                        <div className="time-picker-columns">
                            <div className="time-column">
                                <div className="column-header">Hour</div>
                                <div className="column-items">
                                    {hours.map((hour) => (
                                        <div
                                            key={hour}
                                            className={`time-item ${selectedHour === hour ? 'selected' : ''}`}
                                            onClick={() => handleHourClick(hour)}
                                        >
                                            {hour}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="time-column">
                                <div className="column-header">Minute</div>
                                <div className="column-items">
                                    {minutes.map((minute) => (
                                        <div
                                            key={minute}
                                            className={`time-item ${selectedMinute === minute ? 'selected' : ''}`}
                                            onClick={() => handleMinuteClick(minute)}
                                        >
                                            {minute}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="time-column">
                                <div className="column-header">Period</div>
                                <div className="column-items">
                                    <div
                                        className={`time-item ${selectedPeriod === 'AM' ? 'selected' : ''}`}
                                        onClick={() => handlePeriodClick('AM')}
                                    >
                                        AM
                                    </div>
                                    <div
                                        className={`time-item ${selectedPeriod === 'PM' ? 'selected' : ''}`}
                                        onClick={() => handlePeriodClick('PM')}
                                    >
                                        PM
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {error && errorMessage && (
                <div className="error-message">{errorMessage}</div>
            )}
        </FormGroup>
    );
};

